import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, InfoIcon, SendIcon, SparklesIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Input } from '../ui/Field';
import { ProvenanceBadge } from '../trust/ProvenanceBadge';
import { SURFACE_LABEL, resolveAssistant, suggestedPrompts } from '../../utils/assistant';
import type { AssistantExchange, AssistantSurface, AssistantCard } from '../../types/intelligence';
function surfaceFromPath(pathname: string): AssistantSurface {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/discover')) return 'discover';
  if (pathname.startsWith('/academics')) return 'academics';
  if (pathname.startsWith('/housing')) return 'housing';
  if (pathname.startsWith('/community')) return 'community';
  if (pathname.startsWith('/opportunities')) return 'opportunities';
  if (pathname.startsWith('/marketplace')) return 'marketplace';
  if (pathname.startsWith('/money')) return 'money';
  if (pathname.startsWith('/support')) return 'support';
  if (pathname.startsWith('/profile')) return 'profile';
  if (pathname.startsWith('/notes')) return 'notes';
  if (pathname.startsWith('/services')) return 'services';
  if (pathname.startsWith('/social')) return 'social';
  // Operator consoles are named individually: they hold different records and
  // must never be answered from one another's data.
  if (pathname.startsWith('/property')) return 'property';
  if (pathname.startsWith('/institution')) return 'institution';
  if (pathname.startsWith('/provider')) return 'provider';
  if (pathname.startsWith('/author')) return 'author';
  if (pathname.startsWith('/employer')) return 'employer';
  if (pathname.startsWith('/merchant')) return 'merchant';
  if (pathname.startsWith('/representative')) return 'representative';
  if (pathname.startsWith('/admin')) return 'admin';
  return 'operator';
}
export function AssistantDock() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [thread, setThread] = useState<AssistantExchange[]>([]);
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const surface = useMemo(() => surfaceFromPath(location.pathname), [location.pathname]);
  const prompts = suggestedPrompts(surface);
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    inputRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  function ask(question: string) {
    const trimmed = question.trim();
    if (trimmed.length === 0) return;
    setThread(current => [...current, {
      id: `ex-${Date.now()}`,
      question: trimmed,
      answer: resolveAssistant(trimmed, surface),
      surface
    }]);
    setDraft('');
  }
  function openCard(card: AssistantCard) {
    if (!card.to) return;
    setOpen(false);
    navigate(card.to);
  }
  return <>
      <button type="button" onClick={() => setOpen(true)} aria-expanded={open} className="flex h-9 items-center gap-2 rounded-lg border border-line-strong bg-surface px-2.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:border-ink-subtle hover:text-ink" data-id="element-2008" data-mp="1hd8vu2gkgnnj">
        <SparklesIcon className="h-4 w-4" data-id="element-2009" data-mp="13n5hdxnxhy8n" />
        <span className="hidden lg:inline" data-id="element-2010" data-mp="yoc9qlvbp1ge">Assistant</span>
      </button>

      <AnimatePresence data-id="element-2011" data-mp="ylzmbt6spyvy">
        {open && <motion.aside key="assistant" role="dialog" aria-label="Campuzon Assistant" initial={{
        x: 32,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 24,
        opacity: 0
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full max-w-[420px] flex-col border-l border-line bg-surface shadow-pop" data-id="element-2012" data-mp="tnoajcwxxeb3">
            <header className="flex items-start gap-3 border-b border-line px-5 py-4" data-id="element-2013" data-mp="2k0w2z5xomrdy">
              <div className="min-w-0" data-id="element-2014" data-mp="261edeockba3j">
                <p className="flex items-center gap-2 text-[15px] font-semibold text-ink" data-id="element-2015" data-mp="260d3wob72vnx">
                  <SparklesIcon className="h-4 w-4 text-brand-700" data-id="element-2016" data-mp="2b1itwedh04tr" />
                  Campuzon Assistant
                </p>
                <p className="mt-1 text-[12px] text-ink-muted" data-id="element-2017" data-mp="1w4rvo8fj8z5q">
                  Reading <span className="font-medium text-ink" data-id="element-2018" data-mp="213cuq4j1tcsl">{SURFACE_LABEL[surface]}</span> ·
                  answers come from your authorized data only
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close assistant" className="ml-auto rounded-md p-1 text-ink-subtle transition-colors duration-150 ease-out hover:bg-canvas hover:text-ink" data-id="element-2019" data-mp="3vgkt2668g6iz">
                <XIcon className="h-4 w-4" data-id="element-2020" data-mp="1tj27y0620fin" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-2021" data-mp="1ork7rebuuiyq">
              {thread.length === 0 && <div className="rounded-card border border-line bg-canvas px-4 py-3.5" data-id="element-2022" data-mp="22ixlgbn30mlu">
                  <p className="text-[13px] leading-6 text-ink-muted" data-id="element-2023" data-mp="1yi5bfg6dydoi">
                    Ask about your applications, housing, courses, orders, groups or ledger. I return
                    real records that link into the product, and I say so when Campuzon holds no
                    answer rather than guessing.
                  </p>
                </div>}

              <ul className="space-y-5" data-id="element-2024" data-mp="i5gi0csde9w2">
                {thread.map(exchange => <li key={exchange.id} data-id="element-2025" data-mp="d6n8swk4951p">
                    <p className="ml-auto w-fit max-w-[85%] rounded-2xl bg-brand-700 px-4 py-2.5 text-[13px] leading-6 text-white" data-id="element-2026" data-mp="2d7jzki17olvv">
                      {exchange.question}
                    </p>

                    <div className="mt-3 space-y-3" data-id="element-2027" data-mp="2m772j89l9zr1">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-2028" data-mp="2m9kxlh49rbyl">
                        <p className="text-[13px] leading-6 text-ink" data-id="element-2029" data-mp="2r84irfb1gjpw">{exchange.answer.text}</p>
                        <ProvenanceBadge provenance={exchange.answer.provenance} short data-id="element-2030" data-mp="2w4uuxrjq2v61" />
                      </div>

                      {exchange.answer.cards.length > 0 && <ul className="space-y-2" data-id="element-2031" data-mp="35v7ilsmu34ge">
                          {exchange.answer.cards.map(card => <li key={card.id} data-id="element-2032" data-mp="39vrfqw7eq82j">
                              <button type="button" onClick={() => openCard(card)} disabled={!card.to} className="w-full rounded-card border border-line bg-surface px-4 py-3 text-left transition-colors duration-150 ease-out hover:border-ink-subtle disabled:pointer-events-none" data-id="element-2033" data-mp="3ew5j40y2ctu8">
                                <span className="flex items-start gap-2" data-id="element-2034" data-mp="3k212n5zewbev">
                                  <span className="min-w-0 flex-1" data-id="element-2035" data-mp="3fzw6o0lhr2p2">
                                    <span className="block text-[13px] font-semibold text-ink" data-id="element-2036" data-mp="3g1qw9s7pjizh">
                                      {card.title}
                                    </span>
                                    <span className="cz-num mt-0.5 block text-[12px] text-ink-muted" data-id="element-2037" data-mp="bn95rk05wur5">
                                      {card.meta}
                                    </span>
                                    {card.detail && <span className="mt-1 block text-[12px] leading-5 text-ink-subtle" data-id="element-2038" data-mp="zb0gij620zqb">
                                        {card.detail}
                                      </span>}
                                  </span>
                                  {card.to && <ArrowRightIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-2039" data-mp="1j03t3s1a1xbq" />}
                                </span>
                                {card.badge && <span className="mt-2 flex items-center gap-2" data-id="element-2040" data-mp="1wyk9ybsut47l">
                                    <Badge tone={card.badge.tone} data-id="element-2041" data-mp="1wwhehc8xmivh">{card.badge.label}</Badge>
                                    <ProvenanceBadge provenance={card.provenance} short data-id="element-2042" data-mp="21vkgsoxj2azr" />
                                  </span>}
                              </button>
                            </li>)}
                        </ul>}

                      {exchange.answer.confirmation && <div className="rounded-card border border-brand-700 bg-surface px-4 py-3.5" data-id="element-2043" data-mp="gl3jdt5leumr">
                          <p className="text-[13px] font-semibold text-ink" data-id="element-2044" data-mp="2uqs4ugj9dcgd">
                            {exchange.answer.confirmation.action}
                          </p>
                          <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-2045" data-mp="33t1aot3l0hnf">
                            {exchange.answer.confirmation.summary}
                          </p>
                          <dl className="mt-3 divide-y divide-line border-y border-line" data-id="element-2046" data-mp="2c1bxpad2ilqz">
                            {exchange.answer.confirmation.lines.map(line => <div key={line.label} className="flex items-center justify-between gap-3 py-2" data-id="element-2047" data-mp="270nxpz6v2ukm">
                                <dt className="text-[12px] text-ink-muted" data-id="element-2048" data-mp="2g1cg163afh3b">{line.label}</dt>
                                <dd className="cz-num text-[12px] font-medium text-ink" data-id="element-2049" data-mp="1sb8a2s2mn595">
                                  {line.value}
                                </dd>
                              </div>)}
                          </dl>
                          {exchange.answer.confirmation.gateNote && <p className="mt-3 flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-2050" data-mp="3vvj83lrflstm">
                              <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-2051" data-mp="3qw6ac5z0v1c9" />
                              {exchange.answer.confirmation.gateNote}
                            </p>}
                          <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-2052" data-mp="3h6mr2z7ed5gv">
                            <Button size="sm" variant="primary" disabled={exchange.answer.confirmation.gated || confirmed.includes(exchange.answer.confirmation.id)} onClick={() => setConfirmed(current => [...current, exchange.answer.confirmation?.id ?? ''])} data-id="element-2053" data-mp="3c7jsnxlx9xhv">
                              {exchange.answer.confirmation.confirmLabel}
                            </Button>
                            {exchange.answer.confirmation.gated && <Badge tone="warn" data-id="element-2054" data-mp="uy74ln46qeaa">Awaiting licensing and provider readiness</Badge>}
                          </div>
                        </div>}

                      {exchange.answer.refusal && <div className="rounded-card border border-warn-100 bg-warn-50 px-4 py-3.5" data-id="element-2055" data-mp="18szpz26raoos">
                          <p className="flex items-start gap-2 text-[13px] leading-6 text-ink" data-id="element-2056" data-mp="c90rqgq2ogwe">
                            <TriangleAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-2057" data-mp="g9kovkanbkij" />
                            {exchange.answer.refusal.reason}
                          </p>
                          <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-2058" data-mp="1elx1hoguvary">
                            {exchange.answer.refusal.whereToLook}
                          </p>
                        </div>}
                    </div>
                  </li>)}
              </ul>

              <div className="mt-5 border-t border-line pt-4" data-id="element-2059" data-mp="10nyrev9ki0i9">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-2060" data-mp="vom0nzfks9ae">
                  Try
                </p>
                <ul className="mt-2 space-y-1.5" data-id="element-2061" data-mp="19pexiqtcnggo">
                  {prompts.map(prompt => <li key={prompt} data-id="element-2062" data-mp="15kp4d9q2sfzg">
                      <button type="button" onClick={() => ask(prompt)} className="w-full rounded-lg border border-line px-3 py-2 text-left text-[12px] leading-5 text-ink-muted transition-colors duration-150 ease-out hover:border-ink-subtle hover:text-ink" data-id="element-2063" data-mp="2l30l47jk9a03">
                        {prompt}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>

            <div className="border-t border-line px-5 py-3.5" data-id="element-2064" data-mp="2jlboxe1zct4g">
              <form className="flex items-center gap-2" onSubmit={event => {
            event.preventDefault();
            ask(draft);
          }} data-id="element-2065" data-mp="2jnxzzno2xqrf">
                <Input ref={inputRef} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask about your Campuzon data" aria-label="Ask the assistant" data-id="element-2066" data-mp="2xlmhclixufrq" />
                <Button type="submit" variant="primary" icon={<SendIcon className="h-3.5 w-3.5" data-id="element-2068" data-mp="3v1a84aqvgvje" />} data-id="element-2067" data-mp="3q8h11g7xlb45">
                  Ask
                </Button>
              </form>
              <p className="mt-2 text-[11px] leading-5 text-ink-subtle" data-id="element-2069" data-mp="27e9x7xvt3oqc">
                Runs through the Havazon AI seam. It cannot move money, submit an application or
                accept an order — those arrive as confirmations you approve.
              </p>
            </div>
          </motion.aside>}
      </AnimatePresence>
    </>;
}